import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { PostType } from '@/types/post';
import { BasicUserType } from '@/types/user';
import findUser from '@/actions/findUser';

// Reserved paths that should not be treated as usernames
const RESERVED_PATHS = [
  'login',
  'register',
  'signup',
  'signin',
  'explore',
  'search',
  'notifications',
  'messages',
  'settings',
  'profile',
  'admin',
  'api',
  'auth',
  'help',
  'support',
  'about',
  'privacy',
  'terms',
  'contact',
  'home',
  'feed',
  'trending',
  'discover',
  'bookmarks',
  'lists',
  'moments',
  'topics',
  'events',
  'spaces',
  'communities',
  'replies',
  'status',
  'post',
  'posts',
  'user',
  'users',
  'public',
  'static',
  '_next',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml'
];

interface NavigationState {
  view: 'feed' | 'post' | 'profile';
  selectedPost: PostType | null;
  selectedProfile: BasicUserType | null;
  postId: string | null;
  username: string | null;
  profileUsername: string | null;
  // Add post caching for cross-view navigation
  cachedPosts: PostType[];
  cachedProfiles: Map<string, BasicUserType>;
  // Add loading states
  profileLoading: boolean;
  profileError: string | null;
}

export function useNavigation() {
  console.log('🔧 useNavigation hook called');
  
  const router = useRouter();
  const [navigationState, setNavigationState] = useState<NavigationState>({
    view: 'feed',
    selectedPost: null,
    selectedProfile: null,
    postId: null,
    username: null,
    profileUsername: null,
    cachedPosts: [],
    cachedProfiles: new Map(),
    profileLoading: false,
    profileError: null,
  });

  // Parse URL to determine current view
  useEffect(() => {
    const parseUrl = () => {
      const path = window?.location?.pathname;
      const statusMatch = path.match(/^\/([^\/]+)\/status\/([^\/]+)$/);
      const profileMatch = path.match(/^\/([^\/]+)$/);
      
      // Debug logging for URL parsing
      console.log('🔍 URL parsing:', {
        path,
        statusMatch: !!statusMatch,
        profileMatch: !!profileMatch,
        currentView: navigationState.view
      });
      
      if (statusMatch) {
        const [, username, postId] = statusMatch;
        console.log('📍 Status URL detected:', { username, postId });
        setNavigationState(prev => ({
          ...prev,
          view: 'post',
          postId,
          username,
          profileUsername: null,
        }));
      } else if (profileMatch) {
        const [, username] = profileMatch;
        // Check if this is a reserved path
        if (!RESERVED_PATHS.includes(username.toLowerCase())) {
          setNavigationState(prev => ({
            ...prev,
            view: 'profile',
            profileUsername: username,
            postId: null,
            username: null,
          }));
        } else {
          // Reserved path, treat as feed
          setNavigationState(prev => ({
            ...prev,
            view: 'feed',
            postId: null,
            username: null,
            profileUsername: null,
            selectedPost: null,
            selectedProfile: null,
          }));
        }
      } else {
        setNavigationState(prev => ({
          ...prev,
          view: 'feed',
          postId: null,
          username: null,
          profileUsername: null,
          selectedPost: null,
          selectedProfile: null,
        }));
      }
    };

    // Parse initial URL
    parseUrl();

    // Listen for browser back/forward
    const handlePopState = () => {
      parseUrl();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToPost = (post: PostType, sourcePosts?: PostType[]) => {
    const username = post.user?.username || post.user_username || post.username || 'user';
    const url = `/${username}/status/${post.id}`;
    
    // Update URL without page reload
    window?.history?.pushState({}, '', url);
    
    // Cache posts from source if provided (home feed or profile posts)
    const updatedCachedPosts = sourcePosts ? [...sourcePosts] : navigationState.cachedPosts;
    
    // Update navigation state immediately (don't rely on URL parsing)
    setNavigationState(prev => {
      const newState = {
        ...prev,
        view: 'post' as const,
        selectedPost: post,
        selectedProfile: null,
        postId: post.id.toString(),
        username,
        profileUsername: null,
        cachedPosts: updatedCachedPosts,
      };
      
      // Debug logging for navigation state update
      console.log('🚀 Navigation state update:', {
        from: prev.view,
        to: newState.view,
        postId: newState.postId,
        selectedPost: !!newState.selectedPost
      });
      
      return newState;
    });
  };

  const navigateToFeed = () => {
    // Update URL without page reload
    window?.history?.pushState({}, '', '/');
    
    // Update navigation state (preserve caches)
    setNavigationState(prev => ({
      ...prev,
      view: 'feed',
      selectedPost: null,
      selectedProfile: null,
      postId: null,
      username: null,
      profileUsername: null,
    }));
  };

  const navigateToProfile = async (profileUsername: string, profile?: BasicUserType) => {
    const url = `/${profileUsername}`;
    
    // Update URL without page reload
    window?.history?.pushState({}, '', url);
    
    // Check cache first
    const cachedProfile = navigationState.cachedProfiles.get(profileUsername);
    const profileToUse = profile || cachedProfile;
    
    if (profileToUse) {
      // Instant navigation with cached data - immediately update state
      setNavigationState(prev => ({
        ...prev,
        view: 'profile',
        selectedProfile: profileToUse,
        selectedPost: null,
        postId: null,
        username: null,
        profileUsername,
        profileLoading: false,
        profileError: null,
      }));
      
      // Background refresh if using cached data
      if (!profile && cachedProfile) {
        refreshProfile(profileUsername);
      }
    } else {
      // No cached data - load with minimal loading state
      setNavigationState(prev => ({
        ...prev,
        view: 'profile',
        selectedProfile: null,
        selectedPost: null,
        postId: null,
        username: null,
        profileUsername,
        profileLoading: true,
        profileError: null,
      }));
      
      // Load profile data
      await loadProfile(profileUsername);
    }
  };

  const setSelectedPost = (post: PostType | null) => {
    setNavigationState(prev => ({
      ...prev,
      selectedPost: post,
    }));
  };

  const setSelectedProfile = (profile: BasicUserType | null) => {
    setNavigationState(prev => ({
      ...prev,
      selectedProfile: profile,
    }));
  };

  // Helper function to find post in any cached source
  const findCachedPost = (postId: string | number): PostType | null => {
    const id = postId.toString();
    return navigationState.cachedPosts.find(p => p.id.toString() === id) || null;
  };

  // Helper function to update cached posts
  const updateCachedPosts = (posts: PostType[]) => {
    setNavigationState(prev => ({
      ...prev,
      cachedPosts: posts,
    }));
  };

  // Load profile data
  const loadProfile = async (profileUsername: string) => {
    try {
      const profileData = await findUser({ username: profileUsername });
      if (!profileData) {
        throw new Error('Profile not found');
      }
      
      // Cache and set profile
      const updatedProfiles = new Map(navigationState.cachedProfiles);
      updatedProfiles.set(profileUsername, profileData as unknown as BasicUserType);
      
      setNavigationState(prev => ({
        ...prev,
        selectedProfile: profileData as unknown as BasicUserType,
        cachedProfiles: updatedProfiles,
        profileLoading: false,
        profileError: null,
      }));
    } catch (error) {
      setNavigationState(prev => ({
        ...prev,
        profileLoading: false,
        profileError: error instanceof Error ? error.message : 'Failed to load profile',
      }));
    }
  };

  // Background refresh profile
  const refreshProfile = async (profileUsername: string) => {
    try {
      const profileData = await findUser({ username: profileUsername });
      if (profileData) {
        const updatedProfiles = new Map(navigationState.cachedProfiles);
        updatedProfiles.set(profileUsername, profileData as unknown as BasicUserType);
        
        setNavigationState(prev => ({
          ...prev,
          selectedProfile: profileData as unknown as BasicUserType,
          cachedProfiles: updatedProfiles,
        }));
      }
    } catch (error) {
      // Silent fail for background refresh
      console.warn('Background profile refresh failed:', error);
    }
  };

  // Pre-load profile on hover
  const preloadProfile = async (profileUsername: string) => {
    if (!navigationState.cachedProfiles.has(profileUsername)) {
      try {
        const profileData = await findUser({ username: profileUsername });
        if (profileData) {
          const updatedProfiles = new Map(navigationState.cachedProfiles);
          updatedProfiles.set(profileUsername, profileData as unknown as BasicUserType);
          
          setNavigationState(prev => ({
            ...prev,
            cachedProfiles: updatedProfiles,
          }));
        }
      } catch (error) {
        // Silent fail for preload
        console.warn('Profile preload failed:', error);
      }
    }
  };

  return {
    ...navigationState,
    navigateToPost,
    navigateToProfile,
    navigateToFeed,
    setSelectedPost,
    setSelectedProfile,
    findCachedPost,
    updateCachedPosts,
    loadProfile,
    refreshProfile,
    preloadProfile,
  };
}